<template>
  <div
    v-if="modalIsOpened"
    class="fixed inset-0 w-full bg-black/30 z-50"
    @click.self="closeModal"
  >
    <div
      class="fixed inset-0 overflow-y-auto flex items-start justify-center py-8"
    >
      <div class="w-[100vw] max-w-[704px] px-[20px] overflow-hidden m-auto">
        <div class="mx-auto box-effect rounded-[10px] bg-white overflow-auto">
          <div
            class="flex flex-col justify-center items-start gap-[22px] p-[20px]"
          >
            <div class="w-full max-h-[92px] flex flex-col items-center">
              <component
                v-if="icon"
                :is="icon"
                class="w-[50px] h-[50px] text-primary"
              />
              <span class="PC_Body1-SB !text-[#1F1D1F]">{{ label }}</span>
              <span class="PC_caption-R !text-[#1F1D1F]">
                ขั้นตอน : ตรวจสอบข้อมูลในระบบ
              </span>
            </div>

            <div
              class="w-full flex flex-col justify-center items-start gap-[10px] sm:gap-[22px]"
            >
              <div v-if="shelterSelector" class="w-full">
                <SelectBoxComponent
                  :inputId="'shelter_id'"
                  :searchable="true"
                  :allowEmpty="true"
                  :keyValue="'id'"
                  :keyName="'name'"
                  :options="shelterOptionsView"
                  :value="form.shelter_id"
                  :label="'ศูนย์พักพิงที่ต้องการย้ายเข้า'"
                  :placeholder="'เลือกศูนย์พักพิง'"
                  :allData="true"
                  @update:modelValue="
                    (res) => {
                      form.shelter_id = res?.id ?? '';
                      form.shelter_name = res?.name ?? '';
                    }
                  "
                />
              </div>
              <div
                class="w-full flex items-start gap-[10px] flex-wrap sm:flex-nowrap"
              >
                <SelectBoxComponent
                  :selectContainerClass="'w-full sm:max-w-[140px] min-w-[140px]'"
                  :inputId="'prefix_id'"
                  :searchable="true"
                  :allowEmpty="true"
                  :keyValue="'id'"
                  :keyName="'name'"
                  :options="allPrefixView"
                  :value="form.prefix_id"
                  :label="'คำนำหน้า'"
                  :placeholder="'เลือกคำนำหน้า'"
                  :allData="true"
                  @update:modelValue="
                    (res) => {
                      form.prefix_id = res?.id ?? '';
                      form.prefix_name = res?.name ?? '';
                      form.prefix = res ?? '';
                    }
                  "
                />

                <div class="relative w-full">
                  <AutoCompleteInput
                    id="name"
                    label="ชื่อ"
                    placeholder="ชื่อ"
                    v-model="form.first_name"
                    :fetcher="fetchFirstName"
                    :type="1"
                    :itemText="
                      (it) => (typeof it === 'string' ? it : it?.name ?? '')
                    "
                    :itemValue="
                      (it) => (typeof it === 'string' ? it : it?.name ?? '')
                    "
                    notFoundText="ไม่พบข้อมูล"
                    @select="onPickFirst"
                  />
                </div>

                <div class="relative w-full">
                  <AutoCompleteInput
                    id="surname"
                    label="นามสกุล"
                    placeholder="นามสกุล"
                    v-model="form.last_name"
                    :fetcher="fetchLastName"
                    :type="2"
                    :itemText="
                      (it) => (typeof it === 'string' ? it : it?.name ?? '')
                    "
                    :itemValue="
                      (it) => (typeof it === 'string' ? it : it?.name ?? '')
                    "
                    notFoundText="ไม่พบข้อมูล"
                    @select="onPickLast"
                  />
                </div>
              </div>
            </div>

            <div class="w-full flex items-center justify-center gap-[10px]">
              <ButtonComponent
                :label="'ยกเลิก'"
                :bClass="'btn_shadow--cancel'"
                @clicked="closeModal"
              />
              <ButtonComponent
                v-if="shelterSelector"
                :label="'ตรวจสอบ'"
                :bClass="'btn_shadow--save'"
                :disabled="isAllEmpty2"
                @clicked="check"
              />
              <ButtonComponent
                v-else
                :label="'ตรวจสอบ'"
                :bClass="'btn_shadow--save'"
                :disabled="isAllEmpty1"
                @clicked="check"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import { useGlobals } from "../../components/Utilities/javascripts/useGlobals";
import { usePopulationStore } from "@/pinia/population";
import { usePrefixStore } from "@/pinia/prefix";
import { useShelterStore } from "@/pinia/shelter";
import { storeToRefs } from "pinia";

const props = defineProps({
  icon: { type: String, default: "VictimIcon" },
  label: { type: String, default: "เพิ่มผู้ประสบภัย" },
  shelterSelector: { type: Boolean, default: false },
});

const { $validateMsg } = useGlobals();
const populationStore = usePopulationStore();
const prefixStore = usePrefixStore();
const { allPrefix } = storeToRefs(prefixStore);
const shelterStore = useShelterStore();
const { AllShelterTransfer } = storeToRefs(shelterStore);

// form base
const input = {
  shelter_id: "",
  shelter_name: "",
  prefix: null,
  prefix_id: null,
  prefix_name: null,
  first_name: "",
  last_name: "",
};

const form = ref({ ...input });
const errors = ref({ ...input });
const modalIsOpened = ref(false);
const theme = ref(1);

// ป้องกัน options undefined ตอน build
const toArray = (v) => (Array.isArray(v) ? v : []);
const allPrefixView = computed(() => toArray(allPrefix.value));
const shelterOptionsView = computed(() => toArray(AllShelterTransfer.value));

// util เช็กค่าว่าง
const isEmpty = (v) => v === "" || v === null || v === undefined;

const createIsAllEmpty = (excludedKeys = []) =>
  computed(() =>
    Object.entries(form.value)
      .filter(([key]) => !excludedKeys.includes(key))
      .some(([, value]) => isEmpty(value))
  );

const isAllEmpty1 = createIsAllEmpty(["shelter_id", "shelter_name"]);
const isAllEmpty2 = createIsAllEmpty([]);

// modal ctrl
const openModal = (setTheme, set_shelter) => {
  if (setTheme) theme.value = setTheme;

  form.value = {
    shelter_id: "",
    shelter_name: "",
    prefix: null,
    prefix_id: null,
    prefix_name: null,
    first_name: "",
    last_name: "",
  };

  if (set_shelter) {
    form.value.shelter_id = set_shelter.id;
    form.value.shelter_name = set_shelter.name;
  }

  modalIsOpened.value = true;
  document.body.style.overflow = "hidden";
};

const closeModal = () => {
  modalIsOpened.value = false;
  document.body.style.overflow = "auto";
};

const emit = defineEmits(["addVictim", "updateVictim"]);

const check = async () => {
  const body = {
    ...form.value,
    shelter_id: form.value.shelter_id?.id ?? form.value.shelter_id ?? null,
    shelter_name:
      form.value.shelter_id?.shelter_name ||
      form.value.shelter_id?.name ||
      form.value.shelter_name ||
      "",
    prefix_id: form.value.prefix?.id ?? null, // ดึงจาก object prefix
    prefix_name: form.value.prefix?.name ?? "", // เผื่อ backend ต้องใช้
  };

  await populationStore
    .checkNamePopulation(body)
    .then((response) => {
      if (response.status === 200) {
        if (theme.value == 2) {
          const hasShelter = response.data?.data?.has_shelter == 1;
          emit(
            "updateVictim",
            response.data.message,
            response.data.data,
            hasShelter,
            body
          );
          closeModal();
        } else {
          emit("updateVictim", response.data.message, response.data.data, body);
          closeModal();
        }
      }
    })
    .catch((error) => {
      if (error.status === 400) {
        emit("addVictim", error.response?.data?.message, body, false);
        closeModal();
      } else {
        $validateMsg(error.response?.data?.errors, errors.value);
      }
    });
};

// ---------------autoComplete---------------
function normalizeAutoComplete(res) {
  if (Array.isArray(res)) return res;
  const arr = res?.data?.data?.data ?? res?.data?.data ?? [];
  return Array.isArray(arr) ? arr : [];
}
const fetchFirstName = async ({ query, offset }) => {
  await nextTick();
  const q = typeof query === "string" ? query : "";
  const last =
    typeof form.value.last_name === "string" ? form.value.last_name : "";
  const res = await populationStore.autoCompletePopulationName({
    type: 1,
    first_name: q,
    last_name: last,
    offset: offset ?? 0,
  });
  return normalizeAutoComplete(res);
};

const fetchLastName = async ({ query, offset }) => {
  await nextTick();
  const q = typeof query === "string" ? query : "";
  const first =
    typeof form.value.first_name === "string" ? form.value.first_name : "";
  const res = await populationStore.autoCompletePopulationName({
    type: 2,
    first_name: first,
    last_name: q,
    offset: offset ?? 0,
  });
  return normalizeAutoComplete(res);
};
function onPickFirst(_item) {
  /* ถ้าต้องทำอย่างอื่นเพิ่ม ใส่ตรงนี้ */
}
function onPickLast(_item) {
  /* ถ้าต้องทำอย่างอื่นเพิ่ม ใส่ตรงนี้ */
}
// ---------------END autoComplete---------------

defineExpose({ openModal });
</script>

<style lang="scss" scoped>
.title {
  color: #1f1d1f;
  font-family: Kanit;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.1px;
}
.auto_complete {
  width: 100%;
  border: 1px solid #b7b2b5;
  border-radius: 4px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  li {
    padding: 4px 8px;
    cursor: pointer;
    transition: all 0.3s;
  }
  li:hover,
  li.active {
    background: #fbeff8;
  }
}
.dropdown-portal {
  position: fixed;
  z-index: 999999;
  left: 0;
  top: 0;
}
.spinner {
  display: flex;
  justify-content: center;
  padding: 8px;
  pointer-events: none;
}
.loader {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: #9b237b;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
